/**
 * Firebase configuration and initialization
 * 
 * IMPORTANT: This module uses lazy initialization to prevent Electron crashes.
 * All initialization happens on first access, not at module load time.
 */

import {initializeApp, getApps} from 'firebase/app';
import {connectAuthEmulator, getAuth} from 'firebase/auth';
import {connectFirestoreEmulator, getFirestore} from 'firebase/firestore';
import {connectStorageEmulator, getStorage} from 'firebase/storage';

// Safe environment variable access - wrap everything in try-catch
function safeGetEnv(key: string, fallback: string = ''): string {
  try {
    // Vite replaces import.meta.env at build time
    // In Electron, if env vars weren't set during build, they'll be empty strings
    const value = import.meta.env[key];
    if (value && typeof value === 'string' && value.trim() !== '') {
      return value;
    }
    return fallback;
  } catch {
    return fallback;
  }
}

function isElectronEnv(): boolean {
  try {
    if (typeof window === 'undefined') return false;
    if ((window as any).electronAPI !== undefined) return true;
    if (typeof navigator !== 'undefined' && navigator.userAgent) {
      return navigator.userAgent.includes('Electron');
    }
    return false;
  } catch {
    return false;
  }
}

// Get environment variables safely
let useEmulator: boolean;
let projectId: string;

try {
  useEmulator = safeGetEnv('VITE_USE_FIREBASE_EMULATOR', 'false') === 'true';
  projectId = safeGetEnv('VITE_FIREBASE_PROJECT_ID', 'voxapilot-14e46');
} catch (error) {
  console.warn('Failed to read environment variables:', error);
  useEmulator = false;
  projectId = 'voxapilot-14e46';
}

// Firebase config - wrap in try-catch for Electron safety
let apiKey: string;
let firebaseConfig: any;

try {
  apiKey = useEmulator 
    ? 'demo-api-key-for-emulator' 
    : safeGetEnv('VITE_FIREBASE_API_KEY', '');

  // Log the API key status (without exposing the actual key)
  if (isElectronEnv()) {
    console.log('[Firebase Config] Electron detected');
    console.log('[Firebase Config] API Key present:', !!apiKey && apiKey !== '');
    console.log('[Firebase Config] API Key length:', apiKey ? apiKey.length : 0);
    console.log('[Firebase Config] Using emulator:', useEmulator);
    console.log('[Firebase Config] Project ID:', projectId);
  }

  // CRITICAL: Don't use fallback API keys - they're invalid
  // If API key is missing, we need to fail early with a clear error
  if (!useEmulator && (!apiKey || apiKey.trim() === '')) {
    const errorMsg = `
      ⚠️ Firebase API Key Missing!
      
      The Firebase API key is required but was not found.
      
      This usually happens when:
      1. .env.local file doesn't exist in packages/frontend/
      2. VITE_FIREBASE_API_KEY is not set in .env.local
      3. Frontend was built without environment variables
      4. Dev server needs to be restarted after adding .env.local
      
      To fix this:
      1. Create .env.local in packages/frontend/ with:
         VITE_FIREBASE_API_KEY=your-actual-api-key-here
         VITE_FIREBASE_PROJECT_ID=voxapilot-14e46
         VITE_FIREBASE_AUTH_DOMAIN=voxapilot-14e46.firebaseapp.com
         VITE_FIREBASE_STORAGE_BUCKET=voxapilot-14e46.appspot.com
         VITE_FIREBASE_APP_ID=your-app-id
      
      2. Restart the dev server:
         Stop the server (Ctrl+C) and run: npm run dev
      
      3. Verify in Firebase Console:
         - Go to Project Settings > General > Your apps
         - Copy the API key from the web app config
         - Make sure authorized domains include your domain
      
      See docs/FIREBASE-WEB-APP-SETUP.md for detailed instructions.
    `;
    
    console.error(errorMsg);
    
    // In Electron, we can't throw (would crash), but we can log and use a placeholder
    // The app will fail when trying to use Firebase, but at least it won't crash on startup
    if (isElectronEnv()) {
      console.error('[Firebase Config] ⚠️ WARNING: Using placeholder config - Firebase will fail!');
      // Use a clearly invalid key so Firebase fails with a clear error
      apiKey = 'MISSING_API_KEY_PLEASE_SET_VITE_FIREBASE_API_KEY';
    } else {
      // In web browser, throw immediately
      throw new Error('Firebase API key is required. Please set VITE_FIREBASE_API_KEY in .env.local and restart the dev server');
    }
  }
  
  // Validate API key format (basic check)
  if (!useEmulator && apiKey && apiKey.length < 20) {
    console.warn('[Firebase Config] ⚠️ API key seems too short. Please verify it\'s correct.');
  }

  firebaseConfig = {
    apiKey: apiKey,
    authDomain: useEmulator
      ? `${projectId}.firebaseapp.com`
      : safeGetEnv('VITE_FIREBASE_AUTH_DOMAIN', `${projectId}.firebaseapp.com`),
    projectId: projectId,
    storageBucket: safeGetEnv('VITE_FIREBASE_STORAGE_BUCKET', `${projectId}.appspot.com`),
    ...(useEmulator ? {} : {
      appId: safeGetEnv('VITE_FIREBASE_APP_ID', ''),
      measurementId: safeGetEnv('VITE_FIREBASE_MEASUREMENT_ID', ''),
    }),
  };
  
  // Log final config (without exposing sensitive data)
  if (isElectronEnv()) {
    console.log('[Firebase Config] Final config:', {
      apiKey: apiKey ? `${apiKey.substring(0, 10)}...` : 'MISSING',
      authDomain: firebaseConfig.authDomain,
      projectId: firebaseConfig.projectId,
      hasAppId: !!firebaseConfig.appId,
    });
  }
} catch (error) {
  console.error('[Firebase Config] Config creation failed:', error);
  // Don't use fallback - it will cause invalid-api-key error
  // Instead, use a clearly invalid config so the error is obvious
  firebaseConfig = {
    apiKey: 'CONFIG_ERROR_CHECK_LOGS',
    authDomain: `${projectId}.firebaseapp.com`,
    projectId: projectId,
    storageBucket: `${projectId}.appspot.com`,
  };
}

// Lazy Firebase initialization to prevent Electron crashes
let appInstance: ReturnType<typeof initializeApp> | null = null;

function getApp(): ReturnType<typeof initializeApp> {
  if (!appInstance) {
    try {
      appInstance = getApps()[0];
      if (!appInstance) {
        // Log before initializing
        if (isElectronEnv()) {
          console.log('[Firebase] Initializing Firebase app...');
        }
        appInstance = initializeApp(firebaseConfig);
        if (isElectronEnv()) {
          console.log('[Firebase] Firebase app initialized successfully');
        }
      }

      // Connect to emulators if enabled
      if (useEmulator) {
        try {
          const auth = getAuth(appInstance);
          try {
            connectAuthEmulator(auth, 'http://localhost:9099', {disableWarnings: true});
          } catch (e) {
            // Already connected, ignore
          }

          const db = getFirestore(appInstance);
          try {
            connectFirestoreEmulator(db, 'localhost', 8080);
          } catch (e) {
            // Already connected, ignore
          }

          const storage = getStorage(appInstance);
          try {
            connectStorageEmulator(storage, 'localhost', 9199);
          } catch (e) {
            // Already connected, ignore
          }
        } catch (error) {
          console.warn('Error connecting to emulators:', error);
        }
      }
    } catch (error) {
      console.error('[Firebase] Initialization failed:', error);
      // Don't create fallback - let the error propagate so user knows what's wrong
      throw error;
    }
  }
  return appInstance;
}

// Export app as a Proxy to enable lazy initialization
export const app = new Proxy({} as ReturnType<typeof initializeApp>, {
  get(_target, prop) {
    return (getApp() as any)[prop];
  }
});

// Lazy API client initialization - don't import shared package at module load
let apiInstance: any = null;
let sharedPackagePromise: Promise<any> | null = null;

async function loadSharedPackage() {
  if (!sharedPackagePromise) {
    sharedPackagePromise = import('@voxapilot/shared').catch((error) => {
      console.error('[API] Failed to load shared package:', error);
      console.error('[API] Error details:', error.message, error.stack);
      return null;
    });
  }
  const shared = await sharedPackagePromise;
  if (!shared) {
    console.error('[API] Shared package is null - check if @voxapilot/shared is properly installed');
  } else {
    console.log('[API] Shared package loaded, available exports:', Object.keys(shared).filter(k => !k.startsWith('_')));
  }
  return shared;
}

async function getApiBaseUrlSafe(): Promise<string> {
  if (isElectronEnv()) {
    // Electron: use fallback URL, never call getApiBaseUrl()
    return useEmulator
      ? `http://localhost:5001/${projectId}/us-central1`
      : `https://us-central1-${projectId}.cloudfunctions.net`;
  }
  
  // Web browser: try to use getApiBaseUrl from shared package
  try {
    const shared = await loadSharedPackage();
    if (shared?.getApiBaseUrl) {
      if (useEmulator) {
        return `http://localhost:5001/${projectId}/us-central1`;
      }
      return shared.getApiBaseUrl();
    }
  } catch (error) {
    console.warn('Failed to get API base URL from shared package:', error);
  }
  
  // Fallback
  return useEmulator
    ? `http://localhost:5001/${projectId}/us-central1`
    : `https://us-central1-${projectId}.cloudfunctions.net`;
}

async function initializeApi(): Promise<any> {
  if (apiInstance) {
    console.log('[API] Using existing API instance');
    return apiInstance;
  }
  
  try {
    console.log('[API] Initializing API clients...');
    const apiBaseUrl = await getApiBaseUrlSafe();
    console.log('[API] Base URL:', apiBaseUrl);
    const shared = await loadSharedPackage();
    
    if (!shared) {
      throw new Error('Shared package failed to load');
    }
    
    if (!shared.createApiClients) {
      console.error('[API] createApiClients not found in shared package');
      console.error('[API] Available exports:', Object.keys(shared));
      throw new Error('createApiClients not available in shared package');
    }
    
    console.log('[API] Creating API clients with base URL:', apiBaseUrl);
    apiInstance = shared.createApiClients(apiBaseUrl);
    console.log('[API] API clients initialized successfully');
    console.log('[API] Available clients:', Object.keys(apiInstance));
    
    // Verify study client exists
    if (!apiInstance.study) {
      console.error('[API] WARNING: study client not found in initialized API!');
      console.error('[API] Available clients:', Object.keys(apiInstance));
    } else {
      console.log('[API] Study client verified:', typeof apiInstance.study, Object.keys(apiInstance.study || {}));
    }
    
    return apiInstance;
  } catch (error: any) {
    console.error('[API] Failed to create API clients:', error);
    console.error('[API] Error message:', error?.message);
    console.error('[API] Error stack:', error?.stack);
    // Return a stub that will fail gracefully
    apiInstance = {
      session: {
        createSession: () => Promise.reject(new Error('API not initialized')),
        getSession: () => Promise.reject(new Error('API not initialized')),
        deleteSession: () => Promise.reject(new Error('API not initialized')),
      },
      auth: {
        getProfile: () => Promise.reject(new Error('API not initialized')),
        updateProfile: () => Promise.reject(new Error('API not initialized')),
      },
      study: {
        uploadDocument: () => Promise.reject(new Error('API not initialized')),
        getDocument: () => Promise.reject(new Error('API not initialized')),
        listDocuments: () => Promise.reject(new Error('API not initialized')),
        deleteDocument: () => Promise.reject(new Error('API not initialized')),
        analyzeDocument: () => Promise.reject(new Error('API not initialized')),
        sendChatMessage: () => Promise.reject(new Error('API not initialized')),
        getChatHistory: () => Promise.reject(new Error('API not initialized')),
        generateFlashcards: () => Promise.reject(new Error('API not initialized')),
        getFlashcards: () => Promise.reject(new Error('API not initialized')),
        updateFlashcardReview: () => Promise.reject(new Error('API not initialized')),
      },
    };
    return apiInstance;
  }
}

// Create a stub client that waits for initialization
function createStubClient(clientName: string): any {
  let realClient: any = null;
  let initPromise: Promise<any> | null = null;
  
  const ensureInitialized = async () => {
    if (realClient) return realClient;
    if (!initPromise) {
      initPromise = initializeApi().then((initializedApi) => {
        const client = initializedApi[clientName];
        if (!client) {
          console.error(`[API] Client '${clientName}' not found in initialized API. Available clients:`, Object.keys(initializedApi));
          throw new Error(`API client '${clientName}' not found after initialization`);
        }
        realClient = client;
        return realClient;
      });
    }
    return initPromise;
  };
  
  // Return a Proxy that intercepts method calls
  return new Proxy({} as any, {
    get(_target, prop) {
      // If we have the real client, return it directly
      if (realClient) {
        const value = realClient[prop as string];
        if (value !== undefined) {
          return value;
        }
      }
      
      // Otherwise, return a function that waits for initialization
      const methodName = String(prop);
      return async (...args: any[]) => {
        try {
          console.log(`[API] Calling ${clientName}.${methodName} (initializing if needed)...`);
          const client = await ensureInitialized();
          if (!client) {
            throw new Error(`API client '${clientName}' failed to initialize`);
          }
          const method = client[methodName];
          if (typeof method === 'function') {
            return method.apply(client, args);
          }
          if (method !== undefined) {
            return method;
          }
          throw new Error(`Method '${methodName}' not found on ${clientName} client`);
        } catch (error) {
          console.error(`[API] Error calling ${clientName}.${methodName}:`, error);
          throw error;
        }
      };
    }
  });
}

// Export API as a Proxy that initializes on first access
export const api = new Proxy({} as any, {
  get(_target, prop) {
    // If API is already initialized, return the client directly
    if (apiInstance) {
      const client = apiInstance[prop as string];
      if (client !== undefined) {
        return client;
      }
    }
    
    // Otherwise, return a stub client that initializes on first method call
    const stubClient = createStubClient(prop as string);
    if (stubClient) {
      return stubClient;
    }
    
    // Fallback: return undefined (shouldn't happen, but prevents crashes)
    console.warn(`API client '${String(prop)}' not available`);
    return undefined;
  }
});
